import {
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleGate } from '@/hooks/useAccess';
import { MESSAGE_UNREAD_READ_MODULES } from '@/lib/modules';
import { MessagesService, ApiError } from '@/api';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { errorToastText, extractBackendError } from '@/lib/translateBackendError';
import {
  conversationKeys,
  messageKeys,
  type SendMessageRequest,
  type EditMessageRequest,
  type Message,
  type ConversationDetail,
} from '@/types/messaging';
import { models_SenderType } from '@/api';
import { NAV_BADGE_POLL_MS, visiblePoll } from '@/lib/polling';

/**
 * Hook to fetch unread message count for a user type.
 *
 * @param userType - User type (contact or employee)
 * @param options.audience - `family` (parent correspondence, the default),
 *   `staff` (staff-internal threads) or `all` (both, for the sidebar badge).
 *   Each inbox tab shows its own badge (#1544).
 * @returns TanStack Query result with unread count data
 */
export function useUnreadCount(
  userType: 'contact' | 'employee' = 'contact',
  options?: { enabled?: boolean; audience?: 'family' | 'staff' | 'all' },
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadUnread = useModuleGate(MESSAGE_UNREAD_READ_MODULES);
  const audience = options?.audience ?? 'family';

  return useQuery({
    // The audience follows the `unreadCount(userType)` prefix, so the existing
    // prefix invalidations refresh every badge.
    queryKey: [...messageKeys.unreadCount(userType), audience] as const,
    // No `groupIds`: the user is resolved from the bearer token (#1156). The count
    // is per audience, matching the inbox tab it badges (#1543, #1544); without
    // the param the backend counts every audience.
    queryFn: () =>
      MessagesService.getApiV1MessagesUnreadCount(undefined, audience === 'all' ? undefined : audience),
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady && canReadUnread,
    staleTime: 30000, // 30 seconds
    // A badge: the WebSocket only runs on the messaging page, so poll and
    // refresh when the tab comes back.
    ...visiblePoll(NAV_BADGE_POLL_MS),
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to send a message to a conversation with optimistic update.
 *
 * @param conversationId - The conversation ID to send the message to
 * @returns Mutation hook for sending messages
 */
export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: SendMessageRequest) =>
      MessagesService.postApiV1ConversationsMessages(conversationId, request),
    onMutate: async (newMessage) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: conversationKeys.detail(conversationId),
      });

      // Snapshot the previous value. The detail cache holds infinite-query
      // page data since issue #1297.
      const previousConversation = queryClient.getQueryData<
        InfiniteData<ConversationDetail>
      >(conversationKeys.detail(conversationId));

      // Optimistically update the cache
      if (previousConversation?.pages.length) {
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          conversation_id: conversationId,
          content: newMessage.content,
          sender_type: models_SenderType.SenderTypeEmployee,
          created_at: new Date().toISOString(),
        };

        // `pages[0]` is the NEWEST page and every page is ascending, so the
        // optimistic message is appended to it.
        queryClient.setQueryData<InfiniteData<ConversationDetail>>(
          conversationKeys.detail(conversationId),
          {
            ...previousConversation,
            pages: previousConversation.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    messages: [...(page.messages || []), optimisticMessage],
                    message_count: (page.message_count || 0) + 1,
                    last_message_at: new Date().toISOString(),
                  }
                : page
            ),
          }
        );
      }

      return { previousConversation };
    },
    onError: (_error, _newMessage, context) => {
      // Rollback on error
      if (context?.previousConversation) {
        queryClient.setQueryData(
          conversationKeys.detail(conversationId),
          context.previousConversation
        );
      }
      toast.error(i18n._('messages.send.error'));
    },
    onSettled: () => {
      // Invalidate to refetch the true data
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.lists(),
      });
    },
    onSuccess: () => {
      toast.success(i18n._('messages.send.success'));
    },
  });
}

/**
 * Hook to edit a message within the 15-minute edit window.
 *
 * @returns Mutation hook for editing messages
 */
export function useEditMessage() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: EditMessageRequest }) =>
      MessagesService.putApiV1Messages(id, request),
    onSuccess: () => {
      // Invalidate all conversation details since we don't know which one
      queryClient.invalidateQueries({
        queryKey: conversationKeys.details(),
      });
      toast.success(i18n._('messages.edit.success'));
    },
    onError: (error) => {
      if (extractBackendError(error).code === 'EDIT_WINDOW_EXPIRED') {
        toast.error(i18n._('messages.edit.windowExpired'));
        return;
      }

      toast.error(errorToastText(error, i18n, i18n._('messages.edit.error')));
    },
  });
}

/**
 * Hook to delete a message.
 * Hard delete within 15 minutes, soft delete after.
 *
 * @returns Mutation hook for deleting messages
 */
export function useDeleteMessage() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => MessagesService.deleteApiV1Messages(id),
    onSuccess: () => {
      // Invalidate all conversation details
      queryClient.invalidateQueries({
        queryKey: conversationKeys.details(),
      });
      toast.success(i18n._('messages.delete.success'));
    },
    onError: () => {
      toast.error(i18n._('messages.delete.error'));
    },
  });
}

// Re-export query keys for use in other hooks
export { messageKeys };
